import moment from 'moment';
import { period } from 'utils/constants';
import api from 'utils/api';
import { browserTimeZone } from 'utils/time';
import { legacyBoundary, scheduleToApi } from './schedule';

const SCHEMA_VERSION = 2;

const periodToApi = {
  [period.daily]: 'DAILY',
  [period.weekly]: 'WEEKLY',
  [period.total]: 'TOTAL',
};

const periodFromApi = Object.fromEntries(
  Object.entries(periodToApi).map(([code, name]) => [name, Number(code)])
);

const locationToApi = {
  GLOBAL: 'GLOBAL',
  US: 'US',
  state: 'STATE',
  local: 'LOCAL',
};

const locationFromApi = {
  GLOBAL: 'GLOBAL',
  US: 'US',
  STATE: 'state',
  LOCAL: 'local',
};

const compact = (value) => Object.fromEntries(
  Object.entries(value).filter(([, item]) => item !== undefined)
);

const numberOrUndefined = (value) => (
  value === '' || value == null || Number.isNaN(Number(value))
    ? undefined
    : Number(value)
);

const dateOrUndefined = (value) => {
  if (!value) return undefined;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const categoryIds = (categories) => (Array.isArray(categories) ? categories : [])
  .map((category) => numberOrUndefined(category?.categoryId ?? category))
  .filter((categoryId) => categoryId !== undefined);

const claimPolicyToApi = (budget = {}) => {
  if (budget.unlimitedClaims) {
    return { type: 'UNLIMITED' };
  }
  if (budget.maxClick == null && budget.clickPeriod == null) {
    return undefined;
  }
  return compact({
    type: 'LIMITED',
    maxClaims: numberOrUndefined(budget.maxClick),
    period: periodToApi[budget.clickPeriod],
  });
};

const budgetToApi = (budget) => {
  if (!budget) return undefined;
  return compact({
    maxAmount: numberOrUndefined(budget.maxAmount),
    amountPeriod: periodToApi[budget.amountPeriod],
    claimPolicy: claimPolicyToApi(budget),
  });
};

const locationToDocument = (audience) => {
  if (!audience) return undefined;
  const type = locationToApi[audience.type];
  if (!type) {
    throw new Error(`Unsupported draft location type: ${audience.type}`);
  }
  return compact({
    type,
    states: audience.states,
    localTargets: Array.isArray(audience.local)
      ? audience.local.map((target) => compact({
          addressId: numberOrUndefined(target.addressId ?? target.id?.addressId),
          radiusMiles: numberOrUndefined(target.radius),
          showAddress: target.showAddress,
          showPhone: target.showPhone,
        }))
      : undefined,
  });
};

export const formToDraftOfferDocument = ({
  values,
  businessId,
  currentStep,
  existingDraft,
}) => {
  const description = values?.description || {};
  const details = values?.details || {};
  const code = values?.code || {};
  const budget = values?.budget;
  const existingPayload = existingDraft?.payload || {};

  return compact({
    draftId: existingDraft?.draftId,
    businessId,
    userId: existingDraft?.userId,
    status: 'IN_PROGRESS',
    payload: compact({
      ...existingPayload,
      schemaVersion: SCHEMA_VERSION,
      source: existingPayload.source || 'MANUAL',
      offer: compact({
        fulfillment: description.fulfillment,
        title: description.title,
        categoryIds: categoryIds(description.category),
        keywords: description.keywords,
        headline1: details.headline1,
        headline2: details.headline2,
        description: details.description,
        offerImage: details.offerImage,
        siteUrl: details.siteUrl,
        termsAndConditions: details.termsAndConditions,
        promoCode: code.promoCode,
        barcode: code.barcode,
        qrCode: code.qrCode,
        schedule: scheduleToApi(budget),
        startDate: dateOrUndefined(budget?.startDate),
        endDate: dateOrUndefined(budget?.endDate),
        budget: budgetToApi(budget),
        location: locationToDocument(values?.audience),
      }),
      progress: {
        ...(existingPayload.progress || {}),
        currentStep,
      },
    }),
  });
};

const categoriesFromIds = (ids, businessCategories) => {
  const byId = new Map(
    (Array.isArray(businessCategories) ? businessCategories : [])
      .map((category) => [Number(category.categoryId), category])
  );
  return (Array.isArray(ids) ? ids : []).map(
    (categoryId) => byId.get(Number(categoryId)) || { categoryId }
  );
};

const locationToForm = (location) => {
  if (!location) return undefined;
  const type = locationFromApi[location.type];
  if (!type) {
    throw new Error(`Unsupported draft location type: ${location.type}`);
  }
  return compact({
    type,
    states: location.states,
    local: Array.isArray(location.localTargets)
      ? location.localTargets.map((target) => compact({
          addressId: target.addressId,
          radius: target.radiusMiles,
          showAddress: target.showAddress,
          showPhone: target.showPhone,
        }))
      : undefined,
  });
};

const budgetToForm = (offer) => {
  const budget = offer?.budget;
  if (!budget && !offer?.startDate && !offer?.endDate && !offer?.schedule) return undefined;
  const claims = budget?.claimPolicy;
  return compact({
    maxAmount: budget?.maxAmount,
    amountPeriod: periodFromApi[budget?.amountPeriod],
    unlimitedClaims: claims?.type === 'UNLIMITED',
    maxClick: claims?.type === 'LIMITED' ? claims.maxClaims : undefined,
    clickPeriod: claims?.type === 'LIMITED' ? periodFromApi[claims.period] : undefined,
    startDate: offer?.schedule?.startAt || (offer?.schedule ? undefined : legacyBoundary(offer?.startDate)),
    endDate: offer?.schedule?.endAt || (offer?.schedule ? undefined : legacyBoundary(offer?.endDate, true)),
    timeZone: offer?.schedule?.timeZone || browserTimeZone(),
  });
};

export const draftOfferDocumentToForm = (draft, businessCategories = []) => {
  const offer = draft?.payload?.offer || {};
  return {
    description: compact({
      fulfillment: offer.fulfillment,
      title: offer.title,
      category: categoriesFromIds(offer.categoryIds, businessCategories),
      keywords: offer.keywords,
    }),
    audience: locationToForm(offer.location),
    details: compact({
      headline1: offer.headline1,
      headline2: offer.headline2,
      description: offer.description,
      offerImage: offer.offerImage,
      siteUrl: offer.siteUrl,
      termsAndConditions: offer.termsAndConditions,
    }),
    code: compact({
      promoCode: offer.promoCode,
      barcode: offer.barcode,
      qrCode: offer.qrCode,
    }),
    budget: budgetToForm(offer),
    currentStep: draftCurrentStep(draft),
  };
};

export const draftCurrentStep = (draft) => draft?.payload?.progress?.currentStep ?? 0;

export const reviewDraftOfferDocument = (document) => api.post('/drafts/review', document);
